import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from './constants';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Box,
  Typography,
  InputAdornment,
  ListSubheader,
  Button,
  IconButton,
  Paper,
  createTheme,
  ThemeProvider,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Slider,
  Input,
  CircularProgress,
  Popover,
  FormControlLabel,
  Divider,
  Tooltip,
  Menu,
} from '@mui/material';
import { Search as SearchIcon, FileDownload, Add, Remove, CheckBox, CheckBoxOutlineBlank, ShowChart, TableRows } from '@mui/icons-material';
import MonitoringChartLegend from './MonitoringChartLegend';
import { useAuth } from './AuthContext';
import { getProductViewAccent } from './productViewProjectConfig';
import { DataGrid } from '@mui/x-data-grid';
import { Line } from 'react-chartjs-2';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const options = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index',
    intersect: false,
  },
  stacked: false,
  scales: {
    x: {
      ticks: {
        autoSkip: true,
        maxTicksLimit: 10,
      },
    },
    y: {
      title: {
        display: true,
        text: 'Share %',
        font: {
          size: 18,
          weight: 'bold',
        },
      },
    },
  },
  elements: {
    point: {
      radius: 1,
    },
  },
  layout: {
    padding: {
      right: 10,
      top: 10,
      left: 10,
      bottom: 10,
    },
  },
  
    plugins: {
      legend: {
        display: false, // Disable default legend - using custom interactive legend
      },
      datalabels: {
        align: 'right',
        color: (ctx) => ctx.dataset.borderColor,
        display: (ctx) => ctx.dataIndex === ctx.dataset.data.length - 1,
        formatter: (v, ctx) => ctx.dataset.label,
        offset: 8,
        font: {
          weight: 600,
          size: 10
        },
      }
    }
};

// Memoize chart component to prevent unnecessary re-renders
const SimpleLinePlot2 = React.memo(({ labels, datasets, metadata, visibleDatasets, axisScaleOverrides, showChartLabels = true }) => {
  // Filter datasets based on visibility
  const filteredDatasets = useMemo(() => {
    if (!visibleDatasets) {
      return datasets;
    }
    if (visibleDatasets.size === 0 && datasets && datasets.length > 0) {
      return [];
    }
    return datasets.filter(ds => visibleDatasets.has(ds.label));
  }, [datasets, visibleDatasets]);

  const chartData = useMemo(() => ({
    labels,
    datasets: filteredDatasets,
  }), [labels, filteredDatasets]);

  /** Tighter default Y scale when user has not set max (avoids 0–60% when lines sit under ~15%). */
  const suggestedYMax = useMemo(() => {
    let m = 0;
    for (let di = 0; di < filteredDatasets.length; di++) {
      const pts = filteredDatasets[di].data || [];
      for (let pi = 0; pi < pts.length; pi++) {
        const v = pts[pi];
        if (v != null && typeof v === 'number' && !isNaN(v)) {
          if (v > m) m = v;
        }
      }
    }
    if (m <= 0) return undefined;
    return Math.min(100, m * 1.12 + 1);
  }, [filteredDatasets]);

  const chartOptions = useMemo(() => {
    const yScale = { ...options.scales.y };
    if (typeof axisScaleOverrides?.min === 'number' && !isNaN(axisScaleOverrides.min)) {
      yScale.min = axisScaleOverrides.min;
    }
    if (typeof axisScaleOverrides?.max === 'number' && !isNaN(axisScaleOverrides.max)) {
      yScale.max = axisScaleOverrides.max;
    } else if (suggestedYMax != null) {
      yScale.suggestedMax = suggestedYMax;
    }
    if (typeof axisScaleOverrides?.stepSize === 'number' && !isNaN(axisScaleOverrides.stepSize)) {
      yScale.ticks = { stepSize: axisScaleOverrides.stepSize };
    }

    return {
      ...options,
      scales: {
        ...options.scales,
        y: yScale,
      },
      plugins: {
        ...options.plugins,
        datalabels: {
          ...options.plugins.datalabels,
          display: (ctx) =>
            showChartLabels &&
            Boolean(ctx.dataset.label) &&
            ctx.dataIndex === ctx.dataset.data.length - 1,
        },
        tooltip: {
          ...options.plugins.tooltip,
          itemSort: function(a, b) {
            const aValue = a.parsed.y !== null ? a.parsed.y : -Infinity;
            const bValue = b.parsed.y !== null ? b.parsed.y : -Infinity;
            return bValue - aValue;
          },
          callbacks: {
            label: function(context) {
              const label = context.dataset.label || '';
              const value = context.parsed.y !== null ? context.parsed.y.toFixed(1) : 'N/A';
              const dateIndex = context.dataIndex;
              const date = labels[dateIndex];

              if (metadata && metadata[date] && metadata[date][label]) {
                const { count, totalCount } = metadata[date][label];
                return `${label}: ${value}% (${count}/${totalCount})`;
              }
              return `${label}: ${value}%`;
            }
          }
        }
      }
    };
  }, [labels, metadata, axisScaleOverrides, showChartLabels, suggestedYMax]);

  return <Line data={chartData} options={chartOptions} />;
});

// Utility to transform chart data into table format (Date | Brand1 | Brand2 | ...)
const transformChartDataToTable = (chartData) => {
  if (!chartData || !chartData.labels || !chartData.datasets) {
    return { rows: [], columns: [] };
  }

  const { labels, datasets } = chartData;
  
  // Create column headers: Date + all dataset labels
  const columns = ['Date', ...datasets.map(ds => ds.label)];
  
  // Create rows: for each date, create a row with date and all dataset values
  const rows = labels.map((date, dateIndex) => {
    const row = { Date: date };
    datasets.forEach((dataset) => {
      row[dataset.label] = dataset.data[dateIndex] !== null && dataset.data[dateIndex] !== undefined 
        ? dataset.data[dateIndex].toFixed(2) 
        : '';
    });
    return row;
  });

  return { rows, columns };
};

// Utility to build chart data from raw filtered data with Top N aggregation.
// Uses O(n) index maps — the previous implementation filtered the full table per group×date (very slow).
const transformToChartData = (data, xKey, yKey, groupKey, topN = 7) => {
  if (!data || data.length === 0) return null;

  const labels = Array.from(new Set(data.map((d) => d[xKey]))).sort();
  const groups = [...new Set(data.map((d) => d[groupKey]))];

  const totalByDate = new Map();
  const countByGroupDate = new Map();

  for (let i = 0; i < data.length; i++) {
    const row = data[i];
    const date = row[xKey];
    const group = row[groupKey];
    const t = row._totalCountForDate;
    if (t != null && t !== undefined) {
      totalByDate.set(date, t);
    }
    const key = `${group}|${date}`;
    countByGroupDate.set(key, (countByGroupDate.get(key) || 0) + (row.Count || 0));
  }

  const metadata = {};

  const colors = [
    'rgba(75,192,192,1)', 'rgba(255,99,132,1)', 'rgba(54,162,235,1)',
    'rgba(255,206,86,1)', 'rgba(153,102,255,1)', 'rgba(255,159,64,1)',
  ];

  const makeDataset = (group, idx) => {
    const dataPoints = labels.map((date) => {
      const totalCountForDate = totalByDate.get(date) || 0;
      const aggregatedCount = countByGroupDate.get(`${group}|${date}`) || 0;
      if (aggregatedCount > 0 && totalCountForDate > 0) {
        const aggregatedShare = (aggregatedCount / totalCountForDate) * 100;
        if (!metadata[date]) metadata[date] = {};
        metadata[date][group] = {
          count: aggregatedCount,
          totalCount: totalCountForDate,
        };
        return aggregatedShare;
      }
      return null;
    });

    return {
      label: group,
      data: dataPoints,
      borderColor: colors[idx % colors.length],
      backgroundColor: colors[idx % colors.length].replace('1)', '0.2)'),
      tension: 0.3,
      fill: false,
      spanGaps: false,
    };
  };

  if (groups.length <= topN) {
    const datasets = groups.map((group, idx) => makeDataset(group, idx));
    return { labels, datasets, metadata };
  }

  const sumYByGroup = new Map();
  const countYByGroup = new Map();
  for (let i = 0; i < data.length; i++) {
    const row = data[i];
    const g = row[groupKey];
    const v = row[yKey];
    if (v != null && !isNaN(v)) {
      sumYByGroup.set(g, (sumYByGroup.get(g) || 0) + v);
      countYByGroup.set(g, (countYByGroup.get(g) || 0) + 1);
    }
  }

  const groupAverages = groups.map((group) => ({
    group,
    avgShare: (sumYByGroup.get(group) || 0) / Math.max(1, countYByGroup.get(group) || 0),
  }));

  groupAverages.sort((a, b) => b.avgShare - a.avgShare);
  const topNGroups = groupAverages.slice(0, topN).map((g) => g.group);
  const otherGroups = groupAverages.slice(topN).map((g) => g.group);

  const topNDatasets = topNGroups.map((group, idx) => makeDataset(group, idx));

  if (otherGroups.length > 0) {
    const othersDataPoints = labels.map((date) => {
      let totalCount = 0;
      let totalCountForDate = 0;
      for (let gi = 0; gi < otherGroups.length; gi++) {
        const g = otherGroups[gi];
        const c = countByGroupDate.get(`${g}|${date}`) || 0;
        if (c > 0) {
          totalCount += c;
          if (totalCountForDate === 0) {
            totalCountForDate = totalByDate.get(date) || 0;
          }
        }
      }

      if (totalCount > 0 && totalCountForDate > 0) {
        const aggregatedShare = (totalCount / totalCountForDate) * 100;
        if (!metadata[date]) metadata[date] = {};
        metadata[date].Others = {
          count: totalCount,
          totalCount: totalCountForDate,
        };
        return aggregatedShare;
      }
      return null;
    });

    const othersDataset = {
      label: 'Others',
      data: othersDataPoints,
      borderColor: 'rgba(128, 128, 128, 1)',
      backgroundColor: 'rgba(128, 128, 128, 0.2)',
      tension: 0.3,
      fill: false,
      spanGaps: false,
    };

    return { labels, datasets: [...topNDatasets, othersDataset], metadata };
  }

  return { labels, datasets: topNDatasets, metadata };
};

const TOCView = () => {
  const { projectKey } = useParams();
  const { uiStyle } = useAuth();
  const [lineData, setLineData] = useState(null);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [promoTypes, setPromoTypes] = useState([]);

  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedRetailers, setSelectedRetailers] = useState([]);
  const [selectedPromoTypes, setSelectedPromoTypes] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [selectedManufacturers, setSelectedManufacturers] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [manufacturerSearchTerm, setManufacturerSearchTerm] = useState('');
  const [brandSearchTerm, setBrandSearchTerm] = useState('');
  const [retailerSearchTerm, setRetailerSearchTerm] = useState('');
  const [groupBy, setGroupBy] = useState("Brand");

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  /** Full calendar range for the date slider (set from the first successful load). */
  const [dateExtentMin, setDateExtentMin] = useState(null);
  const [dateExtentMax, setDateExtentMax] = useState(null);
  const hasSetDateExtentRef = useRef(false);
  const [dateSliderValue, setDateSliderValue] = useState([0, 0]);
  const [activeTab, setActiveTab] = useState(0); // 0 = Chart, 1 = Table
  const [topN, setTopN] = useState(7); // Number of top groups to show before aggregating into "Others"
  const [visibleDatasets, setVisibleDatasets] = useState(new Set()); // Track which datasets are visible
  const [legendSearchTerm, setLegendSearchTerm] = useState(''); // Search term for legend
  const [legendExpanded, setLegendExpanded] = useState(true);
  const [axisAnchorEl, setAxisAnchorEl] = useState(null);
  const [yMin, setYMin] = useState('');
  const [yMax, setYMax] = useState('');
  const [yStepSize, setYStepSize] = useState('');
  const [showChartLabels, setShowChartLabels] = useState(true);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const params = new URLSearchParams();
        if (selectedRetailers.length > 0) {
          selectedRetailers.forEach(r => params.append('retailers', r));
        }
        if (selectedCategory) {
          params.append('categories', selectedCategory);
        }
        if (selectedManufacturers.length > 0) {
          selectedManufacturers.forEach(m => params.append('manufacturers', m));
        }
        if (selectedBrands.length > 0) {
          selectedBrands.forEach(b => params.append('brands', b));
        }
        if (selectedPromoTypes.length > 0) {
          selectedPromoTypes.forEach(p => params.append('promo_types', p));
        }
        
        const response = await axios.get(`${API_URL}/toc-products/?${params.toString()}`);
        setProducts(response.data.products || []);
      } catch (error) {
        console.error('Error fetching products:', error);
        setProducts([]);
      }
    };

    fetchProducts();
  }, [selectedRetailers, selectedCategory, selectedManufacturers, selectedBrands, selectedPromoTypes]);

  // Reset selected products when brand, manufacturer, or category changes
  useEffect(() => {
    setSelectedProducts([]);
    setProductSearchTerm('');
  }, [selectedBrands, selectedManufacturers, selectedCategory]);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  /** After a fetch we sync start/end dates (and sometimes promo) from the response; those state updates must not trigger a second identical request. */
  const skipFetchAfterApiSyncRef = useRef(false);

  useEffect(() => {
    if (skipFetchAfterApiSyncRef.current) {
      skipFetchAfterApiSyncRef.current = false;
      return;
    }

    const fetchTOCTimeSeries = async () => {
      try {
        setIsLoading(true);
        // On initial load, fetch without filters to get all filter options
        // After that, fetch with filters
        let requestBody = null;
        if (!isInitialLoad) {
          requestBody = {};
          if (selectedRetailers.length > 0) requestBody.retailers = selectedRetailers;
          if (selectedCategory) requestBody.categories = [selectedCategory];
          if (selectedManufacturers.length > 0) requestBody.manufacturers = selectedManufacturers;
          if (selectedBrands.length > 0) requestBody.brands = selectedBrands;
          if (selectedPromoTypes.length > 0) requestBody.promo_types = selectedPromoTypes;
          if (selectedProducts.length > 0) requestBody.products = selectedProducts;
          if (startDate) requestBody.start_date = startDate;
          if (endDate) requestBody.end_date = endDate;
          
          // Only send if at least one filter is set
          if (Object.keys(requestBody).length === 0) {
            requestBody = null;
          }
        }

        const response = await axios.post(`${API_URL}/toc-timeseries/`, requestBody);
        const data = response.data.toc_timeseries;

        setLineData(data);
        
        // Only update filter options on initial load (when we fetch all data)
        if (isInitialLoad) {
          setCategories([...new Set(data.map(row => row.Category))]);
          setBrands([...new Set(data.map((row) => row.Brand))]);
          setRetailers([...new Set(data.map((row) => row.Retailer || ''))]);
          const promoTypesList = [...new Set(data.map((row) => row["Promo type"] || ''))];
          setPromoTypes(promoTypesList);
          setManufacturers([...new Set(data.map((row) => row.Manufacturer || ''))]);

          // Set "Homepage product" as default selected promo type if it exists
          if (promoTypesList.includes('Homepage product')) {
            setSelectedPromoTypes(['Homepage product']);
          }
        }

        let minTs = Infinity;
        let maxTs = -Infinity;
        for (let i = 0; i < data.length; i++) {
          const t = new Date(data[i].Date).getTime();
          if (!Number.isNaN(t)) {
            if (t < minTs) minTs = t;
            if (t > maxTs) maxTs = t;
          }
        }
        if (minTs !== Infinity && maxTs !== -Infinity) {
          const s = new Date(minTs).toISOString().split('T')[0];
          const e = new Date(maxTs).toISOString().split('T')[0];
          if (isInitialLoad) {
            setStartDate(s);
            setEndDate(e);
          }
          if (!hasSetDateExtentRef.current) {
            hasSetDateExtentRef.current = true;
            setDateExtentMin(dayjs(s));
            setDateExtentMax(dayjs(e));
          }
        }
        
        // After initial load, set flag to false so subsequent fetches use filters
        if (isInitialLoad) {
          setIsInitialLoad(false);
          // Skip one effect run: initial response updates dates, promo default, etc. (deps) without refetching.
          skipFetchAfterApiSyncRef.current = true;
        }
      } catch (error) {
        console.error('Error fetching TOC time series data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTOCTimeSeries();
  }, [isInitialLoad, selectedRetailers, selectedCategory, selectedManufacturers, selectedBrands, selectedPromoTypes, selectedProducts, startDate, endDate]);

  // Memoize normalized data calculation - only recalculate when lineData changes
  const normalizedData = useMemo(() => {
    const filteredData = lineData || [];
    if (!filteredData || filteredData.length === 0) return [];

    // Group by date
    const groupedByDate = {};

    filteredData.forEach((row) => {
      const date = row.Date;
      if (!groupedByDate[date]) groupedByDate[date] = [];
      groupedByDate[date].push(row);
    });

    // For each date, recalculate percentages so they sum to 100%
    const normalized = Object.entries(groupedByDate).flatMap(([date, rowsForDate]) => {
      // Calculate total count for this date (after filtering)
      const totalCountForDate = rowsForDate.reduce((sum, r) => sum + (r.Count || 0), 0);
      
      // Recalculate Share_% based on filtered counts and store count metadata
      const normalizedRows = rowsForDate.map((row) => ({
        ...row,
        'Share_%': totalCountForDate > 0 ? ((row.Count || 0) / totalCountForDate) * 100 : 0,
        _totalCountForDate: totalCountForDate, // Store total count for tooltip
      }));

      return normalizedRows;
    });

    return normalized;
  }, [lineData]);

  // Memoize chart data transformation - only recalculate when dependencies change
  const chartData = useMemo(() => {
    if (!normalizedData || normalizedData.length === 0) return null;
    return transformToChartData(normalizedData, 'Date', 'Share_%', groupBy, topN);
  }, [normalizedData, groupBy, topN]);
  
  // Initialize visible datasets when chartData changes
  useEffect(() => {
    if (chartData && chartData.datasets) {
      // Initialize all datasets as visible
      setVisibleDatasets(new Set(chartData.datasets.map(ds => ds.label)));
    }
  }, [chartData]);
  
  // Memoize table data transformation - only recalculate when chartData changes
  const tableData = useMemo(() => {
    if (!chartData) return { rows: [], columns: [] };
    return transformChartDataToTable(chartData);
  }, [chartData]);

  const totalGroupCount = useMemo(() => {
    if (!normalizedData?.length) return 0;
    return new Set(normalizedData.map((d) => d[groupBy])).size;
  }, [normalizedData, groupBy]);

  const axisScaleOverrides = useMemo(() => {
    const min = yMin !== '' && !isNaN(Number(yMin)) ? Number(yMin) : undefined;
    const max = yMax !== '' && !isNaN(Number(yMax)) ? Number(yMax) : undefined;
    const stepSize = yStepSize !== '' && !isNaN(Number(yStepSize)) ? Number(yStepSize) : undefined;
    return { min, max, stepSize };
  }, [yMin, yMax, yStepSize]);

  const axisSettingsActive =
    yMin !== '' || yMax !== '' || yStepSize !== '' || !showChartLabels;

  const tocAccent = useMemo(() => getProductViewAccent(projectKey, uiStyle), [projectKey, uiStyle]);
  const tocTheme = useMemo(
    () =>
      createTheme({
        palette: {
          primary: {
            main: tocAccent.main,
          },
        },
      }),
    [tocAccent.main]
  );

  const availableDates = useMemo(() => {
    if (!dateExtentMin || !dateExtentMax) return [];
    const dates = [];
    let current = dateExtentMin.startOf('day');
    const end = dateExtentMax.startOf('day');
    while (current.isBefore(end) || current.isSame(end, 'day')) {
      dates.push(current);
      current = current.add(1, 'day');
    }
    return dates;
  }, [dateExtentMin, dateExtentMax]);

  useEffect(() => {
    if (!availableDates.length || !startDate || !endDate) return;
    const sd = dayjs(startDate);
    const ed = dayjs(endDate);
    if (!sd.isValid() || !ed.isValid()) return;
    const startIdx = availableDates.findIndex((d) => d.isSame(sd, 'day'));
    const endIdx = availableDates.findIndex((d) => d.isSame(ed, 'day'));
    setDateSliderValue([
      startIdx === -1 ? 0 : startIdx,
      endIdx === -1 ? availableDates.length - 1 : endIdx,
    ]);
  }, [availableDates, startDate, endDate]);

  const handleDateSliderChange = useCallback((_, newValue) => {
    setDateSliderValue(newValue);
  }, []);

  const handleDateSliderCommit = useCallback(
    (_, newValue) => {
      if (!availableDates.length) return;
      setStartDate(availableDates[newValue[0]].format('YYYY-MM-DD'));
      setEndDate(availableDates[newValue[1]].format('YYYY-MM-DD'));
    },
    [availableDates]
  );

  const handleStartDatePickerChange = useCallback(
    (v) => {
      if (!v) return;
      const s = v.format('YYYY-MM-DD');
      setStartDate(s);
      if (endDate && s > endDate) setEndDate(s);
    },
    [endDate]
  );

  const handleEndDatePickerChange = useCallback(
    (v) => {
      if (!v) return;
      const e = v.format('YYYY-MM-DD');
      setEndDate(e);
      if (startDate && e < startDate) setStartDate(e);
    },
    [startDate]
  );

  const filterControlSx = {
    '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
    '& .MuiInputBase-input': { padding: '4px 8px' },
    '& .MuiInputLabel-root': { fontSize: '0.65rem', transform: 'translate(14px, 8px) scale(1)' },
    '& .MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.85)' },
  };

  // Memoize export functions
  const handleExportCSV = useCallback(() => {
    if (!tableData.rows.length || !tableData.columns.length) {
      alert('No data to export');
      return;
    }

    // Create CSV content
    const csvHeaders = tableData.columns.join(',');
    const csvRows = tableData.rows.map(row => {
      return tableData.columns.map(col => {
        const value = row[col] || '';
        // Escape commas and quotes in CSV
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',');
    });
    
    const csvContent = [csvHeaders, ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `toc_chart_data_${new Date().toISOString().split('T')[0]}.csv`);
  }, [tableData]);

  const handleExportXLSX = useCallback(() => {
    if (!tableData.rows.length || !tableData.columns.length) {
      alert('No data to export');
      return;
    }

    // Prepare worksheet data
    const worksheetData = [
      tableData.columns, // Header row
      ...tableData.rows.map(row => tableData.columns.map(col => row[col] || ''))
    ];

    // Create workbook and worksheet
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Chart Data');

    // Write file
    XLSX.writeFile(wb, `toc_chart_data_${new Date().toISOString().split('T')[0]}.xlsx`);
  }, [tableData]);

  // Legend interaction handlers
  const handleToggleDataset = useCallback((label) => {
    setVisibleDatasets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(label)) {
        newSet.delete(label);
      } else {
        newSet.add(label);
      }
      return newSet;
    });
  }, []);

  const handleShowAll = useCallback(() => {
    if (chartData && chartData.datasets) {
      setVisibleDatasets(new Set(chartData.datasets.map(ds => ds.label)));
    }
  }, [chartData]);

  const handleHideAll = useCallback(() => {
    setVisibleDatasets(new Set());
  }, []);

  return (
    <Box sx={{ width: '100%', maxWidth: '1400px', px: 3, py: 2 }}>
      <ThemeProvider theme={tocTheme}>
        <Paper
          elevation={0}
          sx={{
            p: 1.25,
            mb: 2,
            mt: 1,
            backgroundColor: '#f5f5f5',
            border: '1px solid #e0e0e0',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 1.25,
              alignItems: 'start',
            }}
          >
            {/* Column 1: Country + Retailer */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, overflow: 'hidden' }}>
              <FormControl fullWidth size="small" disabled sx={filterControlSx}>
                <InputLabel id="toc-country-select-label">Country</InputLabel>
                <Select labelId="toc-country-select-label" value="Sweden" label="Country">
                  <MenuItem value="Sweden">Sweden</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" sx={filterControlSx}>
                <InputLabel id="retailer-multiselect-label">Retailer</InputLabel>
                <Select
                  labelId="retailer-multiselect-label"
                  multiple
                  value={selectedRetailers}
                  onChange={(e) => setSelectedRetailers(e.target.value)}
                  input={<OutlinedInput label="Retailer" />}
                  renderValue={(selected) => selected.join(', ')}
                  sx={{ '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                  MenuProps={{
                    PaperProps: { style: { maxHeight: 400, width: 280 } },
                    autoFocus: false,
                  }}
                  onClose={() => setRetailerSearchTerm('')}
                >
                  <ListSubheader>
                    <TextField
                      size="small"
                      autoFocus
                      placeholder="Search retailers..."
                      fullWidth
                      value={retailerSearchTerm}
                      onChange={(e) => setRetailerSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </ListSubheader>
                  {retailers
                    .filter(
                      (retailer) =>
                        retailer &&
                        typeof retailer === 'string' &&
                        retailer.toLowerCase().includes(retailerSearchTerm.toLowerCase())
                    )
                    .map((retailer) => (
                      <MenuItem
                        key={retailer}
                        value={retailer}
                        dense
                        sx={{
                          py: 0.25,
                          minHeight: 'auto',
                          '& .MuiListItemText-primary': { fontSize: '0.875rem' },
                          '& .MuiCheckbox-root': { py: 0.5 },
                        }}
                      >
                        <Checkbox checked={selectedRetailers.includes(retailer)} size="small" sx={{ py: 0 }} />
                        <ListItemText primary={retailer} sx={{ my: 0 }} />
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>

            {/* Column 2: Manufacturer + Brand + Product(s) */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
              <FormControl fullWidth size="small" sx={filterControlSx}>
                <InputLabel id="manufacturer-multiselect-label">Manufacturer</InputLabel>
                <Select
                  labelId="manufacturer-multiselect-label"
                  multiple
                  value={selectedManufacturers}
                  onChange={(e) => setSelectedManufacturers(e.target.value)}
                  input={<OutlinedInput label="Manufacturer" />}
                  renderValue={(selected) => selected.join(', ')}
                  sx={{ '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                  MenuProps={{
                    PaperProps: { style: { maxHeight: 400, width: 280 } },
                    autoFocus: false,
                  }}
                  onClose={() => setManufacturerSearchTerm('')}
                >
                  <ListSubheader>
                    <TextField
                      size="small"
                      autoFocus
                      placeholder="Search manufacturers..."
                      fullWidth
                      value={manufacturerSearchTerm}
                      onChange={(e) => setManufacturerSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </ListSubheader>
                  {manufacturers
                    .filter(
                      (manufacturer) =>
                        manufacturer &&
                        typeof manufacturer === 'string' &&
                        manufacturer.toLowerCase().includes(manufacturerSearchTerm.toLowerCase())
                    )
                    .map((manufacturer) => (
                      <MenuItem
                        key={manufacturer}
                        value={manufacturer}
                        dense
                        sx={{
                          py: 0.25,
                          minHeight: 'auto',
                          '& .MuiListItemText-primary': { fontSize: '0.875rem' },
                          '& .MuiCheckbox-root': { py: 0.5 },
                        }}
                      >
                        <Checkbox checked={selectedManufacturers.includes(manufacturer)} size="small" sx={{ py: 0 }} />
                        <ListItemText primary={manufacturer} sx={{ my: 0 }} />
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" sx={filterControlSx}>
                <InputLabel id="brand-multiselect-label">Brand</InputLabel>
                <Select
                  labelId="brand-multiselect-label"
                  multiple
                  value={selectedBrands}
                  onChange={(e) => setSelectedBrands(e.target.value)}
                  input={<OutlinedInput label="Brand" />}
                  renderValue={(selected) => selected.join(', ')}
                  sx={{ '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                  MenuProps={{
                    PaperProps: { style: { maxHeight: 400, width: 280 } },
                    autoFocus: false,
                  }}
                  onClose={() => setBrandSearchTerm('')}
                >
                  <ListSubheader>
                    <TextField
                      size="small"
                      autoFocus
                      placeholder="Search brands..."
                      fullWidth
                      value={brandSearchTerm}
                      onChange={(e) => setBrandSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </ListSubheader>
                  {brands
                    .filter(
                      (brand) =>
                        brand && typeof brand === 'string' && brand.toLowerCase().includes(brandSearchTerm.toLowerCase())
                    )
                    .map((brand) => (
                      <MenuItem
                        key={brand}
                        value={brand}
                        dense
                        sx={{
                          py: 0.25,
                          minHeight: 'auto',
                          '& .MuiListItemText-primary': { fontSize: '0.875rem' },
                          '& .MuiCheckbox-root': { py: 0.5 },
                        }}
                      >
                        <Checkbox checked={selectedBrands.includes(brand)} size="small" sx={{ py: 0 }} />
                        <ListItemText primary={brand} sx={{ my: 0 }} />
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" sx={filterControlSx}>
                <InputLabel id="product-multiselect-label">Product(s)</InputLabel>
                <Select
                  labelId="product-multiselect-label"
                  multiple
                  value={selectedProducts}
                  onChange={(e) => setSelectedProducts(e.target.value)}
                  input={<OutlinedInput label="Product(s)" />}
                  renderValue={(selected) => selected.join(', ')}
                  sx={{ '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                  MenuProps={{
                    PaperProps: { style: { maxHeight: 400, width: 280 } },
                    autoFocus: false,
                  }}
                  onClose={() => setProductSearchTerm('')}
                >
                  <ListSubheader>
                    <TextField
                      size="small"
                      autoFocus
                      placeholder="Search products..."
                      fullWidth
                      value={productSearchTerm}
                      onChange={(e) => setProductSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </ListSubheader>
                  {products
                    .filter(
                      (product) =>
                        product &&
                        typeof product === 'string' &&
                        product.toLowerCase().includes(productSearchTerm.toLowerCase())
                    )
                    .map((product, idx) => (
                      <MenuItem
                        key={idx}
                        value={product}
                        dense
                        sx={{
                          py: 0.25,
                          minHeight: 'auto',
                          '& .MuiListItemText-primary': { fontSize: '0.875rem' },
                          '& .MuiCheckbox-root': { py: 0.5 },
                        }}
                      >
                        <Checkbox checked={selectedProducts.includes(product)} size="small" sx={{ py: 0 }} />
                        <ListItemText primary={product} sx={{ my: 0 }} />
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>

            {/* Column 3: Group by + Category + Promo type */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
              <FormControl fullWidth size="small" sx={filterControlSx}>
                <InputLabel id="group-by-select-label">Group by</InputLabel>
                <Select
                  labelId="group-by-select-label"
                  value={groupBy}
                  label="Group by"
                  onChange={(e) => setGroupBy(e.target.value)}
                >
                  <MenuItem value="Brand">Brand</MenuItem>
                  <MenuItem value="Manufacturer">Manufacturer</MenuItem>
                  <MenuItem value="Category">Category</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" sx={filterControlSx}>
                <InputLabel id="category-select-label">Category</InputLabel>
                <Select
                  labelId="category-select-label"
                  value={selectedCategory}
                  label="Category"
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {categories.map((category, idx) => (
                    <MenuItem key={idx} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" sx={filterControlSx}>
                <InputLabel id="promo-multiselect-label">Promo type</InputLabel>
                <Select
                  labelId="promo-multiselect-label"
                  multiple
                  value={selectedPromoTypes}
                  onChange={(e) => setSelectedPromoTypes(e.target.value)}
                  input={<OutlinedInput label="Promo type" />}
                  renderValue={(selected) => selected.join(', ')}
                  sx={{ '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                  MenuProps={{ PaperProps: { style: { maxHeight: 400, width: 280 } }, autoFocus: false }}
                >
                  {promoTypes.map((promo) => (
                    <MenuItem key={promo} value={promo} dense sx={{ py: 0.25, minHeight: 'auto' }}>
                      <Checkbox checked={selectedPromoTypes.includes(promo)} size="small" sx={{ py: 0 }} />
                      <ListItemText primary={promo} sx={{ my: 0, '& .MuiListItemText-primary': { fontSize: '0.875rem' } }} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Column 4: Date range + slider */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                  <DatePicker
                    label="Start"
                    value={startDate ? dayjs(startDate) : null}
                    onChange={handleStartDatePickerChange}
                    format="DD/MM/YY"
                    minDate={dateExtentMin || undefined}
                    maxDate={dateExtentMax || undefined}
                    slotProps={{
                      textField: {
                        size: 'small',
                        InputLabelProps: { sx: { fontSize: '0.65rem' } },
                        sx: {
                          width: 112,
                          '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
                          '& .MuiInputBase-input': { padding: '4px 4px 4px 8px' },
                          '& .MuiInputAdornment-root': { ml: 0 },
                        },
                      },
                    }}
                  />
                  <DatePicker
                    label="End"
                    value={endDate ? dayjs(endDate) : null}
                    onChange={handleEndDatePickerChange}
                    format="DD/MM/YY"
                    minDate={dateExtentMin || undefined}
                    maxDate={dateExtentMax || undefined}
                    slotProps={{
                      textField: {
                        size: 'small',
                        InputLabelProps: { sx: { fontSize: '0.65rem' } },
                        sx: {
                          width: 112,
                          '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
                          '& .MuiInputBase-input': { padding: '4px 4px 4px 8px' },
                          '& .MuiInputAdornment-root': { ml: 0 },
                        },
                      },
                    }}
                  />
                </Box>
                <Box sx={{ width: '100%', maxWidth: 240, px: 0.5 }}>
                  <Slider
                    value={Array.isArray(dateSliderValue) ? dateSliderValue : [dateSliderValue, dateSliderValue]}
                    onChange={handleDateSliderChange}
                    onChangeCommitted={handleDateSliderCommit}
                    min={0}
                    max={Math.max(availableDates.length - 1, 0)}
                    step={1}
                    disableSwap
                    valueLabelDisplay="off"
                    disabled={availableDates.length < 2}
                    sx={{
                      color: tocAccent.main,
                      '& .MuiSlider-track': { backgroundColor: tocAccent.main },
                      '& .MuiSlider-rail': { backgroundColor: tocAccent.rail },
                      '& .MuiSlider-thumb': { borderColor: tocAccent.main },
                    }}
                  />
                </Box>
              </Box>
            </LocalizationProvider>
          </Box>
        </Paper>
      </ThemeProvider>

      <Box sx={{ mt: 2, position: 'relative' }}>
        <ThemeProvider theme={tocTheme}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, position: 'relative', width: '100%' }}>
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 0.5,
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Total Ownership KPI
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}
                  >
                    Track brand, manufacturer, and category share over time
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    flexShrink: 0,
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                  }}
                >
                  {activeTab === 0 && (
                    <>
                      <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                        Show Top:
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setTopN((prev) => Math.max(1, prev - 1))}
                        disabled={topN <= 1}
                      >
                        <Remove fontSize="small" />
                      </IconButton>
                      <Input
                        value={topN}
                        size="small"
                        onChange={(e) => {
                          const value = parseInt(e.target.value, 10);
                          if (!isNaN(value) && value >= 1 && value <= 50) {
                            setTopN(value);
                          }
                        }}
                        inputProps={{
                          step: 1,
                          min: 1,
                          max: 50,
                          type: 'number',
                          style: { textAlign: 'center', width: '50px' },
                        }}
                        sx={{ width: '60px' }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => setTopN((prev) => Math.min(50, prev + 1))}
                        disabled={topN >= 50}
                      >
                        <Add fontSize="small" />
                      </IconButton>
                      {chartData?.datasets?.length > 0 && totalGroupCount > 0 && (
                        <Typography variant="caption" sx={{ whiteSpace: 'nowrap', color: 'text.secondary', ml: 0.5 }}>
                          Showing {chartData.datasets.length}/{totalGroupCount} lines
                        </Typography>
                      )}
                    </>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FileDownload fontSize="small" />}
                    disabled={!tableData.rows.length}
                    onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                    sx={{ fontSize: '0.75rem', py: 0.25, px: 1, minWidth: 0, whiteSpace: 'nowrap' }}
                  >
                    Export Data
                  </Button>
                  <Menu
                    anchorEl={exportMenuAnchor}
                    open={Boolean(exportMenuAnchor)}
                    onClose={() => setExportMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <MenuItem
                      dense
                      onClick={() => {
                        handleExportCSV();
                        setExportMenuAnchor(null);
                      }}
                    >
                      CSV
                    </MenuItem>
                    <MenuItem
                      dense
                      onClick={() => {
                        handleExportXLSX();
                        setExportMenuAnchor(null);
                      }}
                    >
                      XLSX
                    </MenuItem>
                  </Menu>
                  <Tooltip title="Chart view" arrow>
                    <IconButton
                      size="small"
                      onClick={() => setActiveTab(0)}
                      sx={{ color: activeTab === 0 ? tocAccent.main : 'text.disabled' }}
                    >
                      <ShowChart fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Table view" arrow>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => setActiveTab(1)}
                        disabled={!tableData.rows.length}
                        sx={{ color: activeTab === 1 ? tocAccent.main : 'text.disabled' }}
                      >
                        <TableRows fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>

              {activeTab === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box
                    sx={{
                      resize: 'vertical',
                      overflow: 'hidden',
                      minHeight: 200,
                      height: 528,
                      display: 'flex',
                      flexDirection: 'column',
                      border: '1px solid transparent',
                      borderBottomColor: 'divider',
                      pb: '6px',
                    }}
                  >
                    <Box sx={{ position: 'relative', flex: 1, minHeight: 0, minWidth: 0 }}>
                      {chartData ? (
                        <SimpleLinePlot2
                          labels={chartData.labels}
                          datasets={chartData.datasets}
                          metadata={chartData.metadata}
                          visibleDatasets={visibleDatasets}
                          axisScaleOverrides={axisScaleOverrides}
                          showChartLabels={showChartLabels}
                        />
                      ) : (
                        <Box
                          sx={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                          }}
                        >
                          {/* Empty chart placeholder */}
                        </Box>
                      )}
                      {isLoading && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            zIndex: 10,
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      )}
                    </Box>
                  </Box>
                  {chartData && chartData.datasets && chartData.datasets.length > 0 && (
                    <>
                      <MonitoringChartLegend
                        datasets={chartData.datasets}
                        visibleDatasets={visibleDatasets}
                        onToggleDataset={handleToggleDataset}
                        onShowAll={handleShowAll}
                        onHideAll={handleHideAll}
                        searchTerm={legendSearchTerm}
                        onSearchChange={setLegendSearchTerm}
                        legendExpanded={legendExpanded}
                        onLegendExpandToggle={() => setLegendExpanded((v) => !v)}
                        onAxisSettingsOpen={(el) => setAxisAnchorEl(el)}
                        axisSettingsActive={axisSettingsActive}
                      />
                      <Popover
                        open={Boolean(axisAnchorEl)}
                        anchorEl={axisAnchorEl}
                        onClose={() => setAxisAnchorEl(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      >
                        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, width: 240 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              color: 'text.secondary',
                              letterSpacing: '0.04em',
                            }}
                          >
                            Y-Axis Settings
                          </Typography>
                          <TextField
                            label="Min value"
                            size="small"
                            type="number"
                            value={yMin}
                            onChange={(e) => setYMin(e.target.value)}
                            placeholder="Auto"
                            InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
                          />
                          <TextField
                            label="Max value"
                            size="small"
                            type="number"
                            value={yMax}
                            onChange={(e) => setYMax(e.target.value)}
                            placeholder="Auto"
                            InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
                          />
                          <TextField
                            label="Tick step size"
                            size="small"
                            type="number"
                            value={yStepSize}
                            onChange={(e) => setYStepSize(e.target.value)}
                            placeholder="Auto"
                            InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={showChartLabels}
                                onChange={(e) => setShowChartLabels(e.target.checked)}
                                size="small"
                              />
                            }
                            label="Show chart labels"
                            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                          />
                          <Divider />
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setYMin('');
                              setYMax('');
                              setYStepSize('');
                              setShowChartLabels(true);
                            }}
                            sx={{ fontSize: '0.7rem', py: 0.25 }}
                          >
                            Reset all to auto
                          </Button>
                        </Box>
                      </Popover>
                    </>
                  )}
                </Box>
              )}

              {activeTab === 1 && tableData.rows.length > 0 && (
                <Box sx={{ width: '100%', position: 'relative' }}>
                  <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {tableData.columns.map((col) => (
                      <TableCell
                        key={col}
                        sx={{
                          fontWeight: 'bold',
                          backgroundColor: '#f5f5f5',
                          minWidth: col === 'Date' ? 120 : 100,
                          position: 'sticky',
                          top: 0,
                          zIndex: 1,
                        }}
                      >
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData.rows.map((row, idx) => (
                    <TableRow key={idx} hover>
                      {tableData.columns.map((col) => (
                        <TableCell key={col}>
                          {row[col]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
                  </TableContainer>
                  {isLoading && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        zIndex: 10,
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </ThemeProvider>
      </Box>

      {/* Banner Data Table - Only show when Chart tab is active */}
      {activeTab === 0 && normalizedData && normalizedData.length > 0 && (
        <Box sx={{ mt: 4, width: '100%' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Banner Data Table
          </Typography>
          <DataGrid
            rows={normalizedData.map((row, index) => ({
              id: index,
              Date: row.Date,
              Brand: row.Brand,
              Manufacturer: row.Manufacturer,
              Category: row.Category,
              Retailer: row.Retailer || '',
              'Promo type': row['Promo type'] || '',
              Count: row.Count || 0,
              'Share_%': row['Share_%'] ? row['Share_%'].toFixed(1) : '0.0',
            }))}
            columns={[
              { field: 'Date', headerName: 'Date', width: 120, sortable: true },
              { field: 'Brand', headerName: 'Brand', width: 150, sortable: true },
              { field: 'Manufacturer', headerName: 'Manufacturer', width: 180, sortable: true },
              { field: 'Category', headerName: 'Category', width: 150, sortable: true },
              { field: 'Retailer', headerName: 'Retailer', width: 180, sortable: true },
              { field: 'Promo type', headerName: 'Promo Type', width: 200, sortable: true },
              { field: 'Count', headerName: 'Count', width: 100, type: 'number', sortable: true },
              { field: 'Share_%', headerName: 'Share %', width: 120, type: 'number', sortable: true },
            ]}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 100,
                },
              },
            }}
            pageSizeOptions={[25, 50, 100, 200]}
            disableSelectionOnClick
            density='compact'
            sx={{
              height: '400px',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
                borderBottom: '2px solid #e0e0e0',
              },
              '& .MuiDataGrid-columnSeparator': {
                visibility: 'visible',
                color: '#e0e0e0',
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default TOCView;